import { useEffect, useMemo, useState } from "react";
import { Building2, CheckCircle2, Clock, Edit, Plus, ShieldCheck, Trash2, Users } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { roleLabels, rolePermissions, UserRole } from "@/lib/permissions";
import { lookupCep, lookupCnpj, maskCep, maskCnpj, maskPhone } from "@/lib/brasil";
import { formatAppDateTime } from "@/lib/date-utils";
import { useAutomaticBrazilLookup } from "@/hooks/useAutomaticBrazilLookup";
import { toast } from "sonner";

type ClienteStatus = "Ativo" | "Pendente" | "Bloqueado" | "Cancelado";
type ClinicaStatus = "Ativa" | "Inativa";
type UsuarioStatus = "Ativo" | "Bloqueado";

type ClienteSaas = {
  id: string;
  nome: string;
  dominio: string;
  responsavel_nome?: string;
  email_responsavel: string;
  nome_conta_principal?: string;
  email_conta_principal?: string;
  login_user_id?: string;
  plano: string;
  status_assinatura: ClienteStatus;
  limite_clinicas: number;
  limite_usuarios: number;
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
};

type ClinicaSaas = {
  id: string;
  cliente_id: string;
  nome: string;
  razao_social?: string;
  nome_fantasia?: string;
  cnpj?: string;
  cro_clinica?: string;
  telefone?: string;
  celular?: string;
  email?: string;
  website?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  situacao_cadastral?: string;
  cnae_principal?: string;
  natureza_juridica?: string;
  porte?: string;
  data_abertura?: string;
  responsavel?: string;
  status: ClinicaStatus;
  created_at?: string;
  updated_at?: string;
};

type UsuarioClinica = {
  id: string;
  cliente_id: string;
  clinica_id: string;
  clinicas_ids?: string[];
  nome: string;
  email: string;
  papel: UserRole;
  status: UsuarioStatus;
  login_user_id?: string;
  created_at?: string;
  updated_at?: string;
};

type ResetRequest = {
  id: string;
  email: string;
  status: string;
  created_at: string;
  resolved_at?: string;
};

const emptyCliente: Omit<ClienteSaas, "id"> = {
  nome: "",
  dominio: "",
  responsavel_nome: "",
  email_responsavel: "",
  plano: "Profissional",
  status_assinatura: "Pendente",
  limite_clinicas: 1,
  limite_usuarios: 3,
  observacoes: "",
};

const emptyClinica: Omit<ClinicaSaas, "id"> = {
  cliente_id: "",
  nome: "",
  razao_social: "",
  nome_fantasia: "",
  cnpj: "",
  cro_clinica: "",
  telefone: "",
  celular: "",
  email: "",
  website: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
  cep: "",
  responsavel: "",
  situacao_cadastral: "",
  cnae_principal: "",
  natureza_juridica: "",
  porte: "",
  data_abertura: "",
  status: "Ativa",
};

const emptyUsuario: Omit<UsuarioClinica, "id"> & { senha?: string } = {
  cliente_id: "",
  clinica_id: "",
  clinicas_ids: [],
  nome: "",
  email: "",
  papel: "cliente_admin",
  status: "Ativo",
  senha: "",
};

const statusVariant = (status: string) => {
  if (["Ativo", "Ativa"].includes(status)) return "default";
  if (["Bloqueado", "Cancelado"].includes(status)) return "destructive";
  return "secondary";
};

const normalizeEmail = (email?: string) => (email || "").trim().toLowerCase();
const errorMessage = (error: unknown) => error instanceof Error ? error.message : "erro inesperado";
const principalNome = (cliente: Partial<ClienteSaas>) => cliente.responsavel_nome || cliente.nome_conta_principal || "";
const principalEmail = (cliente: Partial<ClienteSaas>) => normalizeEmail(cliente.email_responsavel || cliente.email_conta_principal || "");

export function SuperAdmin() {
  const [clientes, setClientes] = useState<ClienteSaas[]>([]);
  const [clinicas, setClinicas] = useState<ClinicaSaas[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioClinica[]>([]);
  const [resetRequests, setResetRequests] = useState<ResetRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [clienteDialog, setClienteDialog] = useState(false);
  const [clinicaDialog, setClinicaDialog] = useState(false);
  const [usuarioDialog, setUsuarioDialog] = useState(false);
  const [editingCliente, setEditingCliente] = useState<ClienteSaas | null>(null);
  const [editingClinica, setEditingClinica] = useState<ClinicaSaas | null>(null);
  const [editingUsuario, setEditingUsuario] = useState<UsuarioClinica | null>(null);
  const [clienteForm, setClienteForm] = useState<typeof emptyCliente & { senha_principal?: string }>(emptyCliente);
  const [clinicaForm, setClinicaForm] = useState(emptyClinica);
  const [usuarioForm, setUsuarioForm] = useState(emptyUsuario);
  const [autoCnpjEnabled, setAutoCnpjEnabled] = useState(false);
  const [autoCepEnabled, setAutoCepEnabled] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [clientesData, clinicasData, usuariosData] = await Promise.all([
        apiList<ClienteSaas>("clientes_saas"),
        apiList<ClinicaSaas>("clinicas"),
        apiList<UsuarioClinica>("usuarios_clinica"),
      ]);
      setClientes(clientesData);
      setClinicas(clinicasData);
      setUsuarios(usuariosData);
      fetch('/api/auth-users.php?action=requests', { credentials: 'include' })
        .then((response) => response.ok ? response.json() : { data: [] })
        .then((payload) => setResetRequests(payload.data || []))
        .catch(() => setResetRequests([]));
    } catch (error: unknown) {
      toast.error("Erro ao carregar painel SaaS: " + errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const metrics = useMemo(() => {
    const ativos = clientes.filter((c) => c.status_assinatura === "Ativo").length;
    const pendentes = clientes.filter((c) => c.status_assinatura === "Pendente").length;
    return { ativos, pendentes, clinicas: clinicas.length, usuarios: usuarios.length };
  }, [clientes, clinicas, usuarios]);

  const openCliente = (cliente?: ClienteSaas) => {
    setEditingCliente(cliente || null);
    setClienteForm(cliente ? {
      ...cliente,
      responsavel_nome: principalNome(cliente),
      email_responsavel: principalEmail(cliente),
      senha_principal: "",
    } : { ...emptyCliente, senha_principal: "" });
    setClienteDialog(true);
  };

  const openClinica = (clinica?: ClinicaSaas, clienteId?: string) => {
    setEditingClinica(clinica || null);
    setClinicaForm(clinica ? { ...clinica } : { ...emptyClinica, cliente_id: clienteId || clientes[0]?.id || "" });
    setAutoCnpjEnabled(false);
    setAutoCepEnabled(false);
    setClinicaDialog(true);
  };

  const openUsuario = (usuario?: UsuarioClinica, clienteId?: string, clinicaId?: string) => {
    setEditingUsuario(usuario || null);
    const resolvedCliente = clienteId || usuario?.cliente_id || clientes[0]?.id || "";
    const resolvedClinica = clinicaId || usuario?.clinica_id || clinicas.find((c) => c.cliente_id === resolvedCliente)?.id || "";
    const selectedClinicas = usuario?.clinicas_ids?.length ? usuario.clinicas_ids : resolvedClinica ? [resolvedClinica] : [];
    setUsuarioForm(usuario ? { ...usuario, clinicas_ids: selectedClinicas, senha: "" } : { ...emptyUsuario, cliente_id: resolvedCliente, clinica_id: resolvedClinica, clinicas_ids: selectedClinicas });
    setUsuarioDialog(true);
  };

  const saveCliente = async () => {
    try {
      const nomePrincipal = principalNome(clienteForm);
      const email = principalEmail(clienteForm);
      if (!nomePrincipal || !email) {
        throw new Error("Informe nome e email da conta principal");
      }
      const emailEmCliente = clientes.some((cliente) => cliente.id !== editingCliente?.id && principalEmail(cliente) === email);
      const emailEmUsuario = usuarios.some((usuario) => normalizeEmail(usuario.email) === email && usuario.login_user_id !== editingCliente?.login_user_id);
      if (emailEmCliente || emailEmUsuario) {
        throw new Error("Email já cadastrado");
      }

      const authResponse = await fetch('/api/auth-users.php?action=upsert', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nomePrincipal,
          email,
          senha: clienteForm.senha_principal || '',
          papel: 'cliente_admin',
          status: clienteForm.status_assinatura === 'Bloqueado' || clienteForm.status_assinatura === 'Cancelado' ? 'Bloqueado' : 'Ativo',
          login_user_id: editingCliente?.login_user_id,
        }),
      });
      const authPayload = await authResponse.json().catch(() => ({}));
      if (!authResponse.ok || authPayload.error) {
        throw new Error(authPayload.error || 'Erro ao salvar login da conta principal');
      }

      const { senha_principal, ...clienteData } = clienteForm;
      const payload = {
        ...clienteData,
        responsavel_nome: nomePrincipal,
        email_responsavel: email,
        nome_conta_principal: nomePrincipal,
        email_conta_principal: email,
        login_user_id: authPayload.login_user_id,
        limite_clinicas: Number(clienteForm.limite_clinicas) || 1,
        limite_usuarios: Number(clienteForm.limite_usuarios) || 1,
      };
      if (editingCliente) await apiUpdate<ClienteSaas>("clientes_saas", editingCliente.id, payload);
      else await apiCreate<ClienteSaas>("clientes_saas", payload);
      toast.success(senha_principal ? "Cliente e senha principal salvos" : "Cliente salvo com sucesso");
      setClienteDialog(false);
      await load();
    } catch (error: unknown) {
      toast.error("Erro ao salvar cliente: " + errorMessage(error));
    }
  };

  const cnpjLookup = useAutomaticBrazilLookup({
    value: clinicaForm.cnpj || "",
    digits: 14,
    enabled: autoCnpjEnabled,
    lookup: lookupCnpj,
    onSuccess: (info) => {
      setClinicaForm((prev) => ({
        ...prev,
        cnpj: info.cnpj,
        razao_social: info.razao_social,
        nome_fantasia: info.nome_fantasia,
        nome: prev.nome || info.nome_fantasia || info.razao_social,
        telefone: info.telefone || prev.telefone,
        email: info.email || prev.email,
        cep: info.cep,
        endereco: info.endereco,
        numero: info.numero,
        complemento: info.complemento,
        bairro: info.bairro,
        cidade: info.cidade,
        estado: info.estado,
        situacao_cadastral: info.situacao_cadastral,
        cnae_principal: info.cnae_principal,
        natureza_juridica: info.natureza_juridica,
        porte: info.porte,
        data_abertura: info.data_abertura,
      }));
      toast.success("CNPJ da clinica preenchido");
    },
    onError: (error) => {
      toast.error("Erro ao buscar CNPJ: " + error.message);
    },
  });

  const cepLookup = useAutomaticBrazilLookup({
    value: clinicaForm.cep || "",
    digits: 8,
    enabled: autoCepEnabled,
    lookup: lookupCep,
    onSuccess: (info) => {
      setClinicaForm((prev) => ({
        ...prev,
        cep: info.cep,
        endereco: info.logradouro || prev.endereco,
        complemento: info.complemento || prev.complemento,
        bairro: info.bairro || prev.bairro,
        cidade: info.cidade || prev.cidade,
        estado: info.estado || prev.estado,
      }));
      toast.success("CEP da clinica preenchido");
    },
    onError: (error) => {
      toast.error("Erro ao buscar CEP: " + error.message);
    },
  });

  const buscarCnpjClinica = () => void cnpjLookup.run();
  const buscarCepClinica = () => void cepLookup.run();

  const saveClinica = async () => {
    try {
      if (editingClinica) await apiUpdate<ClinicaSaas>("clinicas", editingClinica.id, clinicaForm);
      else await apiCreate<ClinicaSaas>("clinicas", clinicaForm);
      toast.success("Clinica salva com sucesso");
      setClinicaDialog(false);
      await load();
    } catch (error: unknown) {
      toast.error("Erro ao salvar clinica: " + errorMessage(error));
    }
  };

  const saveUsuario = async () => {
    try {
      const email = normalizeEmail(usuarioForm.email);
      const selectedClinicas = usuarioForm.clinicas_ids?.length ? usuarioForm.clinicas_ids : usuarioForm.clinica_id ? [usuarioForm.clinica_id] : [];
      if (!usuarioForm.nome.trim() || !email || selectedClinicas.length === 0) {
        throw new Error("Preencha nome, email e ao menos uma clinica");
      }
      const emailEmUsuario = usuarios.some((usuario) => usuario.id !== editingUsuario?.id && normalizeEmail(usuario.email) === email);
      const emailEmCliente = clientes.some((cliente) => normalizeEmail(cliente.email_responsavel) === email && cliente.login_user_id !== editingUsuario?.login_user_id);
      if (emailEmUsuario || emailEmCliente) {
        throw new Error("Email já cadastrado");
      }

      const authResponse = await fetch('/api/auth-users.php?action=upsert', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...usuarioForm, email, clinica_id: selectedClinicas[0], clinicas_ids: selectedClinicas }),
      });
      const authPayload = await authResponse.json().catch(() => ({}));
      if (!authResponse.ok || authPayload.error) {
        throw new Error(authPayload.error || 'Erro ao salvar usuario de login');
      }

      const { senha, ...usuarioData } = usuarioForm;
      usuarioData.clinica_id = selectedClinicas[0] || usuarioData.clinica_id;
      usuarioData.clinicas_ids = selectedClinicas;
      usuarioData.email = email;
      const payload = { ...usuarioData, login_user_id: authPayload.login_user_id };
      if (editingUsuario) await apiUpdate<UsuarioClinica>("usuarios_clinica", editingUsuario.id, payload);
      else await apiCreate<UsuarioClinica>("usuarios_clinica", payload);
      toast.success(senha ? "Usuario e senha salvos com sucesso" : "Usuario salvo com sucesso");
      setUsuarioDialog(false);
      await load();
    } catch (error: unknown) {
      toast.error("Erro ao salvar usuario: " + errorMessage(error));
    }
  };

  const resolveResetRequest = async (id: string) => {
    const response = await fetch('/api/auth-users.php?action=resolve_request', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      toast.success('Solicitacao marcada como resolvida');
      await load();
    }
  };

  const updateClienteStatus = async (cliente: ClienteSaas, status: ClienteStatus) => {
    await apiUpdate<ClienteSaas>("clientes_saas", cliente.id, { status_assinatura: status });
    toast.success(status === "Ativo" ? "Cliente liberado" : "Status atualizado");
    await load();
  };

  const removeRecord = async (entity: string, id: string) => {
    await apiDelete(entity, id);
    toast.success("Registro removido");
    await load();
  };

  const clinicasDoCliente = (clienteId: string) => clinicas.filter((c) => c.cliente_id === clienteId);
  const usuariosDoCliente = (clienteId: string) => usuarios.filter((u) => u.cliente_id === clienteId);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Painel SuperAdmin</h1>
            <p className="text-muted-foreground">O cliente SaaS concentra a conta principal e todas as clinicas/unidades vinculadas.</p>
          </div>
          <Button className="gap-2" onClick={() => openCliente()}>
            <Building2 className="h-4 w-4" />
            Novo cliente
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Clientes</CardTitle></CardHeader><CardContent className="flex items-center gap-3"><Users className="h-5 w-5 text-primary" /><span className="text-2xl font-bold">{clientes.length}</span></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Clinicas</CardTitle></CardHeader><CardContent className="flex items-center gap-3"><Building2 className="h-5 w-5 text-primary" /><span className="text-2xl font-bold">{metrics.clinicas}</span></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Ativos</CardTitle></CardHeader><CardContent className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-green-600" /><span className="text-2xl font-bold">{metrics.ativos}</span></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Pendentes</CardTitle></CardHeader><CardContent className="flex items-center gap-3"><Clock className="h-5 w-5 text-amber-600" /><span className="text-2xl font-bold">{metrics.pendentes}</span></CardContent></Card>
        </div>

        <Tabs defaultValue="clientes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="clientes">Clientes</TabsTrigger>
            <TabsTrigger value="permissoes">Permissoes</TabsTrigger>
            <TabsTrigger value="resets">Resets</TabsTrigger>
          </TabsList>

          <TabsContent value="clientes">
            <Card>
              <CardHeader><CardTitle>Clientes SaaS</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {loading && <p className="text-sm text-muted-foreground">Carregando clientes...</p>}
                {!loading && clientes.length === 0 && <p className="text-sm text-muted-foreground">Nenhum cliente cadastrado.</p>}
                {clientes.map((cliente) => {
                  const totalClinicas = clinicasDoCliente(cliente.id).length;
                  const totalUsuarios = usuariosDoCliente(cliente.id).length;
                  const limiteClinicasAtingido = totalClinicas >= Number(cliente.limite_clinicas || 0);
                  const limiteUsuariosAtingido = totalUsuarios >= Number(cliente.limite_usuarios || 0);
                  return (
                    <div key={cliente.id} className="space-y-4 rounded-md border p-4">
                      <div className="grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-start">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium">{cliente.nome}</p>
                            <Badge variant={statusVariant(cliente.status_assinatura)}>{cliente.status_assinatura}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">Conta principal: {principalNome(cliente) || "Responsavel nao informado"} - {principalEmail(cliente) || "sem email"}</p>
                          <p className="text-sm text-muted-foreground">Dominio: {cliente.dominio || "Sem dominio"}</p>
                          <p className="mt-1 text-sm text-muted-foreground">Plano {cliente.plano} - {totalClinicas}/{cliente.limite_clinicas} clinicas - {totalUsuarios}/{cliente.limite_usuarios} usuarios</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => updateClienteStatus(cliente, "Ativo")}>Liberar</Button>
                          <Button size="sm" variant="outline" onClick={() => updateClienteStatus(cliente, "Bloqueado")}>Bloquear</Button>
                          <Button size="sm" variant="ghost" onClick={() => openCliente(cliente)}><Edit className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => removeRecord("clientes_saas", cliente.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">Clinicas do cliente</p>
                            <Button size="sm" variant="outline" disabled={limiteClinicasAtingido} onClick={() => openClinica(undefined, cliente.id)}>
                              <Plus className="mr-1 h-3 w-3" /> Clinica
                            </Button>
                          </div>
                          {clinicasDoCliente(cliente.id).map((clinica) => (
                            <div key={clinica.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                              <span>
                                {clinica.nome}
                                <span className="text-muted-foreground"> {clinica.cnpj || "sem CNPJ"} {clinica.cidade ? "- " + clinica.cidade : ""}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <Badge variant={statusVariant(clinica.status)}>{clinica.status}</Badge>
                                <Button size="sm" variant="ghost" onClick={() => openClinica(clinica)}><Edit className="h-3 w-3" /></Button>
                              </span>
                            </div>
                          ))}
                          {clinicasDoCliente(cliente.id).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma clinica vinculada.</p>}
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">Contas de acesso</p>
                            <Button size="sm" variant="outline" disabled={limiteUsuariosAtingido || totalClinicas === 0} onClick={() => openUsuario(undefined, cliente.id)}>
                              <Plus className="mr-1 h-3 w-3" /> Usuario
                            </Button>
                          </div>
                          {usuariosDoCliente(cliente.id).map((usuario) => (
                            <div key={usuario.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                              <span>{usuario.nome} <span className="text-muted-foreground">{roleLabels[usuario.papel]}</span></span>
                              <span className="flex items-center gap-1">
                                <Badge variant={statusVariant(usuario.status)}>{usuario.status}</Badge>
                                <Button size="sm" variant="ghost" onClick={() => openUsuario(usuario)}><Edit className="h-3 w-3" /></Button>
                              </span>
                            </div>
                          ))}
                          {usuariosDoCliente(cliente.id).length === 0 && <p className="text-sm text-muted-foreground">Nenhum usuario vinculado.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resets">
            <Card>
              <CardHeader><CardTitle>Solicitacoes de reset de senha</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {resetRequests.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma solicitacao de reset.</p>}
                {resetRequests.map((request) => (
                  <div key={request.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium">{request.email}</p>
                      <p className="text-sm text-muted-foreground">{formatAppDateTime(request.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={request.status === 'Resolvido' ? 'default' : 'secondary'}>{request.status}</Badge>
                      {request.status !== 'Resolvido' && <Button size="sm" variant="outline" onClick={() => resolveResetRequest(request.id)}>Marcar resolvido</Button>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissoes">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Perfis e permissoes base</CardTitle></CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {Object.entries(rolePermissions).map(([role, permissions]) => (
                  <div key={role} className="rounded-md border p-4">
                    <p className="font-medium">{roleLabels[role as UserRole]}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{permissions.includes("*") ? "Acesso total ao SaaS" : permissions.join(", ")}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={clienteDialog} onOpenChange={setClienteDialog}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader><DialogTitle>{editingCliente ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Nome</Label><Input value={clienteForm.nome} onChange={(e) => setClienteForm({ ...clienteForm, nome: e.target.value })} /></div>
            <div className="space-y-2"><Label>Dominio</Label><Input value={clienteForm.dominio} onChange={(e) => setClienteForm({ ...clienteForm, dominio: e.target.value })} /></div>
            <div className="space-y-2"><Label>Nome da conta principal</Label><Input value={clienteForm.responsavel_nome || ""} onChange={(e) => setClienteForm({ ...clienteForm, responsavel_nome: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email da conta principal</Label><Input value={clienteForm.email_responsavel} onChange={(e) => setClienteForm({ ...clienteForm, email_responsavel: e.target.value })} /></div>
            <div className="space-y-2 sm:col-span-2"><Label>{editingCliente ? "Nova senha da conta principal (deixe vazio para manter)" : "Senha inicial da conta principal"}</Label><Input type="password" value={clienteForm.senha_principal || ""} onChange={(e) => setClienteForm({ ...clienteForm, senha_principal: e.target.value })} placeholder="Minimo 6 caracteres" /></div>
            <div className="space-y-2"><Label>Plano</Label><Select value={clienteForm.plano} onValueChange={(plano) => setClienteForm({ ...clienteForm, plano })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Implantacao">Implantacao</SelectItem><SelectItem value="Basico">Basico</SelectItem><SelectItem value="Profissional">Profissional</SelectItem><SelectItem value="Enterprise">Enterprise</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Status</Label><Select value={clienteForm.status_assinatura} onValueChange={(status_assinatura: ClienteStatus) => setClienteForm({ ...clienteForm, status_assinatura })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Pendente">Pendente</SelectItem><SelectItem value="Ativo">Ativo</SelectItem><SelectItem value="Bloqueado">Bloqueado</SelectItem><SelectItem value="Cancelado">Cancelado</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Limite clinicas</Label><Input type="number" min="1" value={clienteForm.limite_clinicas} onChange={(e) => setClienteForm({ ...clienteForm, limite_clinicas: Number(e.target.value) })} /></div><div className="space-y-2"><Label>Limite usuarios</Label><Input type="number" min="1" value={clienteForm.limite_usuarios} onChange={(e) => setClienteForm({ ...clienteForm, limite_usuarios: Number(e.target.value) })} /></div></div>
            <div className="space-y-2 sm:col-span-2"><Label>Observacoes</Label><Textarea value={clienteForm.observacoes} onChange={(e) => setClienteForm({ ...clienteForm, observacoes: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setClienteDialog(false)}>Cancelar</Button><Button onClick={saveCliente}>{editingCliente ? "Salvar / resetar senha" : "Criar cliente e acesso"}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={clinicaDialog} onOpenChange={setClinicaDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[760px]">
          <DialogHeader><DialogTitle>{editingClinica ? "Editar clinica do cliente" : "Nova clinica do cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Cliente SaaS</Label><Select value={clinicaForm.cliente_id} onValueChange={(cliente_id) => setClinicaForm({ ...clinicaForm, cliente_id })}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]"><div className="space-y-2"><Label>CNPJ da clinica</Label><Input value={clinicaForm.cnpj || ""} onChange={(e) => { setAutoCnpjEnabled(true); setClinicaForm({ ...clinicaForm, cnpj: maskCnpj(e.target.value) }); }} />{cnpjLookup.loading && <p className="text-xs text-muted-foreground">Consultando dados na Receita Federal...</p>}</div><div className="flex items-end"><Button type="button" variant="outline" onClick={buscarCnpjClinica} disabled={cnpjLookup.loading}>{cnpjLookup.loading ? "Buscando..." : "Consultar novamente"}</Button></div></div>
            <div className="grid gap-3 sm:grid-cols-2"><div className="space-y-2"><Label>Nome fantasia</Label><Input value={clinicaForm.nome_fantasia || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, nome_fantasia: e.target.value, nome: e.target.value })} /></div><div className="space-y-2"><Label>Razao social</Label><Input value={clinicaForm.razao_social || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, razao_social: e.target.value })} /></div></div>
            <div className="grid gap-3 sm:grid-cols-2"><div className="space-y-2"><Label>Nome exibido</Label><Input value={clinicaForm.nome} onChange={(e) => setClinicaForm({ ...clinicaForm, nome: e.target.value })} /></div><div className="space-y-2"><Label>CRO da clinica</Label><Input value={clinicaForm.cro_clinica || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, cro_clinica: e.target.value })} /></div></div>
            <div className="grid gap-3 sm:grid-cols-3"><div className="space-y-2"><Label>Telefone</Label><Input value={clinicaForm.telefone || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, telefone: maskPhone(e.target.value) })} /></div><div className="space-y-2"><Label>Celular</Label><Input value={clinicaForm.celular || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, celular: maskPhone(e.target.value) })} /></div><div className="space-y-2"><Label>Email</Label><Input value={clinicaForm.email || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, email: e.target.value })} /></div></div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]"><div className="space-y-2"><Label>CEP</Label><Input value={clinicaForm.cep || ""} onChange={(e) => { setAutoCepEnabled(true); setClinicaForm({ ...clinicaForm, cep: maskCep(e.target.value) }); }} />{cepLookup.loading && <p className="text-xs text-muted-foreground">Consultando CEP...</p>}</div><div className="flex items-end"><Button type="button" variant="outline" onClick={buscarCepClinica} disabled={cepLookup.loading}>{cepLookup.loading ? "Buscando..." : "Consultar novamente"}</Button></div></div>
            <div className="grid gap-3 sm:grid-cols-[2fr_1fr]"><div className="space-y-2"><Label>Endereco</Label><Input value={clinicaForm.endereco || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, endereco: e.target.value })} /></div><div className="space-y-2"><Label>Numero</Label><Input value={clinicaForm.numero || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, numero: e.target.value })} /></div></div>
            <div className="grid gap-3 sm:grid-cols-3"><div className="space-y-2"><Label>Bairro</Label><Input value={clinicaForm.bairro || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, bairro: e.target.value })} /></div><div className="space-y-2"><Label>Cidade</Label><Input value={clinicaForm.cidade || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, cidade: e.target.value })} /></div><div className="space-y-2"><Label>Estado</Label><Input value={clinicaForm.estado || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, estado: e.target.value.toUpperCase().slice(0, 2) })} /></div></div>
            <div className="space-y-2"><Label>Complemento</Label><Input value={clinicaForm.complemento || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, complemento: e.target.value })} /></div>
            {(clinicaForm.situacao_cadastral || clinicaForm.cnae_principal) && <div className="grid gap-3 rounded-md border bg-muted/20 p-3 text-sm sm:grid-cols-2"><p><strong>Situação:</strong> {clinicaForm.situacao_cadastral || "-"}</p><p><strong>Abertura:</strong> {clinicaForm.data_abertura || "-"}</p><p><strong>Atividade:</strong> {clinicaForm.cnae_principal || "-"}</p><p><strong>Natureza/porte:</strong> {[clinicaForm.natureza_juridica, clinicaForm.porte].filter(Boolean).join(" - ") || "-"}</p></div>}
            <div className="grid gap-3 sm:grid-cols-2"><div className="space-y-2"><Label>Responsavel da unidade</Label><Input value={clinicaForm.responsavel || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, responsavel: e.target.value })} /></div><div className="space-y-2"><Label>Status</Label><Select value={clinicaForm.status} onValueChange={(status: ClinicaStatus) => setClinicaForm({ ...clinicaForm, status })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Ativa">Ativa</SelectItem><SelectItem value="Inativa">Inativa</SelectItem></SelectContent></Select></div></div>
            <div className="space-y-2"><Label>Website</Label><Input value={clinicaForm.website || ""} onChange={(e) => setClinicaForm({ ...clinicaForm, website: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setClinicaDialog(false)}>Cancelar</Button><Button onClick={saveClinica}>Salvar clinica</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={usuarioDialog} onOpenChange={setUsuarioDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingUsuario ? "Editar usuario" : "Novo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Cliente</Label><Select value={usuarioForm.cliente_id} onValueChange={(cliente_id) => setUsuarioForm({ ...usuarioForm, cliente_id, clinica_id: clinicas.find((c) => c.cliente_id === cliente_id)?.id || "" })}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Clinica</Label><Select value={usuarioForm.clinica_id} onValueChange={(clinica_id) => setUsuarioForm({ ...usuarioForm, clinica_id })}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{clinicas.filter((c) => c.cliente_id === usuarioForm.cliente_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Nome</Label><Input value={usuarioForm.nome} onChange={(e) => setUsuarioForm({ ...usuarioForm, nome: e.target.value })} /></div><div className="space-y-2"><Label>Email de login</Label><Input value={usuarioForm.email} onChange={(e) => setUsuarioForm({ ...usuarioForm, email: e.target.value })} /></div></div>
            <div className="space-y-2"><Label>{editingUsuario ? "Nova senha (deixe vazio para manter)" : "Senha inicial"}</Label><Input type="password" value={usuarioForm.senha || ""} onChange={(e) => setUsuarioForm({ ...usuarioForm, senha: e.target.value })} placeholder="Minimo 6 caracteres" /></div>
            <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Papel</Label><Select value={usuarioForm.papel} onValueChange={(papel: UserRole) => setUsuarioForm({ ...usuarioForm, papel })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(roleLabels).map(([role, label]) => <SelectItem key={role} value={role}>{label}</SelectItem>)}</SelectContent></Select></div><div className="space-y-2"><Label>Status</Label><Select value={usuarioForm.status} onValueChange={(status: UsuarioStatus) => setUsuarioForm({ ...usuarioForm, status })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Ativo">Ativo</SelectItem><SelectItem value="Bloqueado">Bloqueado</SelectItem></SelectContent></Select></div></div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setUsuarioDialog(false)}>Cancelar</Button><Button onClick={saveUsuario}>{editingUsuario ? "Salvar / resetar senha" : "Criar acesso"}</Button></div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
